import { useEffect, useState } from "react";
import HomepageCollectionSkeleton from "../HomepageCollectionSkeleton";
import CollectionRail from "../CollectionRail";
import {
  getEnquiredInventories,
  getPreviouslyViewedInventories,
  getShortlistedInventories,
} from "@Pages/Home/helpers/PreviouslyViewedHelper";
import { getLastViewedListings } from "@Pages/Search/data/helper";
import { shallowEqual, useSelector } from "react-redux";
import { AppState } from "@Store/index";
import wrapperHOC from "@Utils/wrapperHOC";
import useCookies from "hooks/useCookies";
import useWhitelabel from "hooks/useWhitelabel";
import useTranslationHook from "hooks/useTranslationHook";
import { CONTINUE_JOURNEY_PILLS } from "constants/index";
//

const PreviouslyViewed = ({ isDesktop, isHomePage = true }: any) => {
  const [PreviouslyViewedPills, setPreviouslyViewedPills] = useState<
    { name: string; key: string }[] | any
  >(null);

  const { isWhitelabel, whitelabelConfig, partner_id } = useWhitelabel();
  const [collections, setCollections]: any = useState(null);
  const [recentlyViewedCityCanonical, setRecenltyViewedCityCanonical]: any = useState();
  const getFirstData = async (pill: any) => {
    const res = await fetchData(pill);
    return res;
  };

  const { isUserLoggedIn, propertyViewedFlag, userCurrency, userLanguage } = useSelector(
    ({ appData }: AppState) => ({
      isUserLoggedIn: appData.isUserLoggedIn,
      propertyViewedFlag: appData.propertyViewedFlag,
      userCurrency: appData.userCurrency,
      userLanguage: appData.userLanguage,
    }),
    shallowEqual,
  );

  const { t } = useTranslationHook();

  const userFlowToken = useCookies("user_flow_token");
  const isCookiePresent = userFlowToken || isUserLoggedIn;
  const isRepeatUser = propertyViewedFlag || isCookiePresent;

  const firstAPIComputations = async () => {
    // Identity + label key both come from the shared constant — `CollectionRail` matches the
    // selected tab on `name`, so the literal cannot live in two places.
    const shortlistedControlObject = CONTINUE_JOURNEY_PILLS.SHORTLISTED;
    const recentlyViewedControlObject = CONTINUE_JOURNEY_PILLS.RECENTLY_VIEWED;
    const enquiryControlObject = CONTINUE_JOURNEY_PILLS.ENQUIRED;
    const Pills: any = [];
    const DataFetched: any = {
      enquired: false,
      recentlyViewed: false,
      shortlisted: false,
    };

    let collectionRes = {};
    let canonicalNameRes = "";
    if (window) {
      if (isCookiePresent) {
        const APIRes = await getFirstData({ key: "enquired" });
        if (
          APIRes?.result?.length &&
          !JSON.stringify(Pills).includes(JSON.stringify(enquiryControlObject))
        ) {
          Pills.push(enquiryControlObject);
          collectionRes = APIRes;
          canonicalNameRes = APIRes?.result?.[0]?.region_parent_city_canonical;
          DataFetched.enquired = true;
        }
      }
      if (propertyViewedFlag) {
        const APIRes = await getFirstData({ key: "recentlyViewed" });
        if (
          APIRes?.result?.length &&
          !JSON.stringify(Pills).includes(JSON.stringify(recentlyViewedControlObject))
        ) {
          Pills.push(recentlyViewedControlObject);
          if (!DataFetched.enquired) {
            collectionRes = APIRes;
            canonicalNameRes = APIRes?.result?.[0]?.region_parent_city_canonical;
            DataFetched.recentlyViewed = true;
          }
        }
      }
      if (isUserLoggedIn) {
        const APIRes = await getFirstData({ key: "shortlisted" });
        if (
          APIRes?.result?.length &&
          !JSON.stringify(Pills).includes(JSON.stringify(shortlistedControlObject))
        ) {
          Pills.push(shortlistedControlObject);
          if (!DataFetched.enquired && !DataFetched.recentlyViewed) {
            collectionRes = APIRes;
            canonicalNameRes = APIRes?.result?.[0]?.region_parent_city_canonical;
            DataFetched.shortlisted = true;
          }
        }
      }
      setCollections(collectionRes);
      setRecenltyViewedCityCanonical(canonicalNameRes);
      setPreviouslyViewedPills(Pills);
    }
  };

  useEffect(() => {
    firstAPIComputations();
  }, []);

  const sortRecentlyViewed = (savedRecentListings: any, res: any) => {
    const listings = res?.result?.filter(
      (listing: any) =>
        !(
          typeof listing.images === "undefined" ||
          (listing.images && listing.images.length === 0) ||
          listing.status !== "active"
        ),
    );
    return listings?.length > 0
      ? listings
          ?.filter((item: any) => savedRecentListings?.includes(item?.canonical_name))
          ?.sort((a: any, b: any) => {
            const indexA = savedRecentListings?.indexOf(a?.canonical_name);
            const indexB = savedRecentListings?.indexOf(b?.canonical_name);
            return indexA - indexB;
          })
      : [];
  };

  const fetchData = async (pill: any) => {
    let res: any = null;
    let savedRecentListings: any = null;
    switch (pill.key) {
      case "recentlyViewed":
        savedRecentListings = await getLastViewedListings(1, 10);
        res = await getPreviouslyViewedInventories(
          savedRecentListings,
          isWhitelabel,
          partner_id,
          userCurrency,
          userLanguage,
        );
        if (res["result"] != "exploreMore")
          res["result"] = sortRecentlyViewed(savedRecentListings, res);
        break;
      case "shortlisted": {
        res = await getShortlistedInventories(userCurrency, userLanguage);
        res?.result.forEach((r: any, i: any) => {
          r.inventory["reviews_count"] = r.reviews_count;
          r.inventory["reviews_rating"] = r.reviews_rating;
          r.inventory["top_meta_distances"] = r?.inventory?.meta?.distances?.slice(0, 3);
          res.result[i] = r.inventory;
        });

        break;
      }
      case "enquired":
        res = await getEnquiredInventories(
          isUserLoggedIn,
          isWhitelabel,
          userCurrency,
          userLanguage,
        );

        break;
      default:
        res = [];
    }
    return res;
  };

  return (
    <>
      {(isRepeatUser && PreviouslyViewedPills?.length) || propertyViewedFlag || userFlowToken ? (
        <>
          {(collections && collections["result"]?.length && PreviouslyViewedPills?.length) ||
          !collections ? (
            <>
              {collections && collections["result"]?.length && PreviouslyViewedPills?.length ? (
                <>
                  <CollectionRail
                    key="previouslyViewed"
                    collection={collections["result"]}
                    title={
                      isHomePage
                        ? t("home.previouslyviewed.previouslyviewedtitle")
                        : t("searchpage.searchlist.previouslyviewed.recentlyviewed")
                    }
                    pills={PreviouslyViewedPills}
                    fetchData={fetchData}
                    showSeeMoreCard={false}
                    recentlyViewedCityCanonical={recentlyViewedCityCanonical}
                    gtagEventAction="recent_journey_property_clicked"
                    gtagEventActionPill="recent_journey_tab_clicked"
                    isDesktop={isDesktop}
                    isShowPills={isHomePage}
                    {...(isWhitelabel && {
                      greyBg: whitelabelConfig?.is_multi_region_enabled,
                    })}
                  />
                </>
              ) : (
                <>
                  <HomepageCollectionSkeleton
                    title={
                      isHomePage
                        ? t("searchpage.searchlist.previouslyviewed.continuewhereyouleftfrom")
                        : t("searchpage.searchlist.previouslyviewed.recentlyviewed")
                    }
                    isDesktop={isDesktop}
                  />
                </>
              )}
            </>
          ) : (
            <></>
          )}
        </>
      ) : (
        <></>
      )}
    </>
  );
};

export default wrapperHOC(PreviouslyViewed, {
  componentName: "PreviouslyViewed-Home",
  showForChina: true,
});
